/**
 * WP-1 — React hook exposing the storefront step-transition emitters.
 *
 * Consumers (storefront-layout cart hooks, SideCart, PostPurchasePage) call
 * these to fire `cart_updated` / `order_created` / `order_updated` / `timeout`
 * transitions plus their plain behavioral mirrors. The hook binds the emitters
 * to the live FunnelProvider session (funnel id, session id, scope, bus) and is
 * stable across renders as long as those inputs are stable.
 */

import { useEffect, useMemo, useRef } from 'react';
import { useOptionalFunnelContext } from './funnel-provider';
import {
  createStepEventEmitters,
  type StepEventEmitters,
} from './events/step-event-emitters';
import { funnelLogger } from './logger';

/**
 * Returns the step emitters, or a no-op set when rendered outside a
 * FunnelProvider (keeps call sites unconditional and safe).
 */
export function useFunnelStepEmitters(): StepEventEmitters {
  const context = useOptionalFunnelContext();

  // Keep the latest assigned funnel id readable from the stable emitter set
  // without re-creating emitters (and their dedupe stores) on every assign.
  const funnelIdRef = useRef<string | null>(context?.assignedFunnelId ?? null);
  funnelIdRef.current = context?.assignedFunnelId ?? null;

  // Likewise expose the active step id so the server can resolve the outgoing
  // edge for cart_updated / order_created / order_updated / timeout. Read via a
  // ref so navigating between steps does not re-create the emitters.
  const currentStepIdRef = useRef<string | undefined>(context?.activeExperience?.stepId);
  currentStepIdRef.current = context?.activeExperience?.stepId;

  return useMemo<StepEventEmitters>(() => {
    if (!context) {
      const noop = async () => null;
      return {
        emitBehavioralEvent: () => undefined,
        emitCartUpdated: noop,
        emitOrderCreated: noop,
        emitOrderUpdated: noop,
        emitTimeout: noop,
      };
    }
    return createStepEventEmitters({
      evaluatePageStep: context.evaluatePageStep,
      eventBus: context.eventBus,
      sessionId: context.sessionId,
      scope: {
        tenantId: context.config.tenantId,
        storeId: context.config.storeId,
      },
      getFunnelId: () => funnelIdRef.current,
      getCurrentStepId: () => currentStepIdRef.current,
      onTransitionError: (event, error) => {
        funnelLogger.warn('Best-effort funnel transition failed after commerce commit', {
          event,
          error: error instanceof Error ? error.message : String(error),
        });
      },
    });
    // Emitters own their dedupe stores; only re-create when the session identity
    // or transport primitives change, not when the assigned funnel flips.
  }, [
    context?.sessionId,
    context?.eventBus,
    context?.evaluatePageStep,
    context?.config.tenantId,
    context?.config.storeId,
    context,
  ]);
}

/**
 * WP-1 — Honor a step's `timeoutSeconds`: fire the `timeout` transition (and
 * `timer_expired` mirror) once, after the configured delay, for the given step.
 *
 * A `timeoutSeconds` of 0/undefined disables the timer. The timer resets when
 * the step id changes and is cleared on unmount. Deduped per (funnel, step) by
 * the underlying emitter, so a re-render cannot double-fire.
 *
 * NB: this is the STEP-level timeout (auto-advance an offer that is ignored).
 * The CountdownTimer *block* trigger is a separate, block-scoped concern — see
 * tss-docs architecture/funnels/funnel-events-model.md.
 */
export function useStepTimeoutEmitter(
  stepId: string | null | undefined,
  timeoutSeconds: number | null | undefined,
): void {
  const { emitTimeout } = useFunnelStepEmitters();

  useEffect(() => {
    if (!stepId || !timeoutSeconds || timeoutSeconds <= 0) return undefined;
    const timer = setTimeout(() => {
      void emitTimeout({ stepId });
    }, timeoutSeconds * 1000);
    return () => clearTimeout(timer);
  }, [stepId, timeoutSeconds, emitTimeout]);
}
